// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useEffect, useState } from 'react';

import {
  type WorkCell,
  type WorkCellId,
  formatDate,
  useEvolvePaginatedGet,
  type WorkOrderStateName,
} from '@evolvemep/api-ts';
import { ActionIcon, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { useFacilityRequired } from 'components/Mantine/GlobalFilters/FacilityRequired';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { KanbanBoard } from 'components/Mantine/Kanban/KanbanBoard';
import type { CardConfigMapper } from 'components/Mantine/Kanban/types';
import { WorkCellsTaskKanbanCard } from 'modules/Shop/Tasks/WorkCellsTaskKanbanCard';
import type { WorkCellsProps } from 'modules/Shop/Tasks/WorkCellTasksPage';
import { WorkCellTasksPauseAll } from 'modules/Shop/Tasks/WorkCellTasksPauseAll';

const workOrderStatesToDisplay: WorkOrderStateName[] = ['Not Started', 'In Progress', 'Request for Cancel'] as const;

export const WorkCellsKanbanView = (props: WorkCellsProps) => {
  const { workOrderStates, taskStatusTypes } = props;
  const { selectedFacility } = useFacilityRequired();
  const { facilityId } = selectedFacility;
  const { workOrderIds, projectIds, assigneeIds } = useGlobalFilters({
    projects: true,
    assignees: 'shop/task/assignedUsers',
    workOrders: {
      hasShopTask: true,
      workOrderStateIds: workOrderStates.filter((s) => workOrderStatesToDisplay.includes(s.name)).map((s) => s.stateId),
    },
  });

  const [lastRefresh, setLastRefresh] = useState(() => new Date());

  const { setDefaultOpts: setDefaultOptsLanes, ...paginatedGetLanes } = useEvolvePaginatedGet('shop/workCell', {
    perPage: 0,
  });
  const { loading, reset, data: workCells } = paginatedGetLanes;
  useEffect(() => {
    setDefaultOptsLanes({
      perPage: 20,
      defaultConfig: {
        params: {
          facilityId,
        },
      },
    });
  }, [facilityId, setDefaultOptsLanes]);

  const getLaneLabel = (workCell: WorkCell) => workCell.workCellName;
  const cardConfigMapper: CardConfigMapper<WorkCell, 'shop/workCell/:workCellId/kanban'> = useCallback(
    (workCell: WorkCell) => ({
      requestConfig: {
        url: `shop/workCell/${workCell.workCellId}/kanban`,
        params: {
          assigneeIds,
          projectIds,
          workOrderIds,
        },
      },
    }),
    [assigneeIds, projectIds, workOrderIds],
  );

  /** Check if any of the provided work cell IDs are not present in the current list of work cells. */
  const checkIfWorkCellsNeedsUpdate = (workCellIds: WorkCellId[]) =>
    !workCellIds.every((id) => workCells.some((workCell) => workCell.workCellId === id));

  return (
    <>
      <BasePageHeader
        title="Tasks"
        topLeftComponent={
          <>
            <ActionIcon
              size="lg"
              variant="light"
              onClick={() => {
                reset();
                setLastRefresh(new Date());
              }}
              disabled={loading}
            >
              <EvolveIcon icon="Refresh" />
            </ActionIcon>
            <Text c="dimmed" fz="sm">
              Last refresh: {formatDate(lastRefresh, 'p')}
            </Text>
          </>
        }
        topRightComponent={
          <WorkCellTasksPauseAll facilityId={facilityId} taskStatusTypes={taskStatusTypes} refresh={reset} />
        }
      />
      <KanbanBoard
        laneIdKey="workCellId"
        getLaneLabel={getLaneLabel}
        paginatedGetLanes={paginatedGetLanes}
        cardIdKey="taskId"
        cardConfigMapper={cardConfigMapper}
      >
        {(task, index, workCell) => (
          <WorkCellsTaskKanbanCard
            key={task.taskId}
            index={index}
            workCellTaskKanban={task}
            onTaskMoved={async (newWorkCellId) => {
              if (checkIfWorkCellsNeedsUpdate([newWorkCellId, workCell.workCellId])) {
                reset();
              }
            }}
            selectedFacility={selectedFacility}
          />
        )}
      </KanbanBoard>
    </>
  );
};
